import * as React from 'react';
import styled from 'styled-components';
import { useSwipeable, EventData } from 'react-swipeable';
import { useCarousel } from './useCarousel';

const StageWrapper = styled.div<{ translateX: number }>`
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
  box-sizing: border-box;
  /* stylelint-disable property-no-unknown */
  * {
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
  }
  .rail {
    transform: ${({ translateX }) => `translateX(-${translateX}px)`};
  }
  /* stylelint-enable property-no-unknown */
`;

export const Stage: React.FC = ({ children }) => {
  const { slidesCount, slidesToShow, wrapperWidth, slidingIndex, setSlidingIndex, centered } = useCarousel();
  const isDraggingRef = React.useRef(false);
  const eventRef = React.useRef<EventData | null>(null);
  const railRef = React.createRef<HTMLDivElement>();

  const swipingDistance = React.useRef(0);
  const sliderItemWidth = wrapperWidth / slidesToShow;

  // This prevents dragging clicks
  const setPointerEvent = (event: 'auto' | 'none') => {
    if (railRef && railRef.current) {
      railRef.current.style.pointerEvents = event;
    }
  };

  // Runs while mouse/touch swipe or drag
  const handleSwiping = (distance: number) => {
    if (railRef && railRef.current) {
      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        setPointerEvent('none');
      }
      const sliderItemWidth = wrapperWidth / slidesToShow;
      const railMaxLeft = sliderItemWidth * (slidesCount - slidesToShow) + sliderItemWidth / 2;
      railRef.current.style.transition = 'all 0ms linear';
      const calculatedLeft = Math.max(
        -railMaxLeft,
        Math.min(wrapperWidth / slidesToShow / 2, -distance + swipingDistance.current),
      );

      railRef.current.style.marginLeft = `${calculatedLeft}px`;
    }
  };

  // Runs after dragging interrupted and sliding index change from outside e.g carousel buttons
  const handleSettle = React.useCallback(
    (distance: number) => {
      if (railRef && railRef.current) {
        const railMaxLeft = sliderItemWidth * (slidesCount - slidesToShow);
        swipingDistance.current = Math.min(0, swipingDistance.current - distance);
        const leftOver = Math.abs(swipingDistance.current) % sliderItemWidth;
        const additional = leftOver > sliderItemWidth / 2 ? sliderItemWidth : 0;
        const fixedDistance = swipingDistance.current - (swipingDistance.current % sliderItemWidth) - additional;
        swipingDistance.current = Math.max(fixedDistance, -railMaxLeft);
        railRef.current.style.transition = 'all 200ms linear';
        railRef.current.style.marginLeft = `${swipingDistance.current}px`;
        setSlidingIndex((swipingDistance.current / sliderItemWidth) * -1);
      }
    },
    [railRef, setSlidingIndex, sliderItemWidth, slidesCount, slidesToShow],
  );

  React.useEffect(() => {
    const nextSwipingDistance = -slidingIndex * sliderItemWidth;
    if (swipingDistance.current !== nextSwipingDistance) {
      swipingDistance.current = nextSwipingDistance;
      handleSettle(0);
    }
  }, [slidingIndex, sliderItemWidth, handleSettle]);

  const stopHorizontalEventPropagation = (e: EventData) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.event.preventDefault();
      e.event.stopPropagation();
    }
  };

  const handlers = useSwipeable({
    onSwiping(e) {
      handleSwiping(e.deltaX);
      stopHorizontalEventPropagation(e);
      eventRef.current = e;
    },
    onSwiped() {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        eventRef.current = null;
        setPointerEvent('auto');
      }
    },
    onSwipedLeft(e) {
      stopHorizontalEventPropagation(e);
      handleSettle(e.deltaX);
    },
    onSwipedRight(e) {
      stopHorizontalEventPropagation(e);
      handleSettle(e.deltaX);
    },
    trackMouse: true,
    preventDefaultTouchmoveEvent: false,
  });

  return (
    <StageWrapper {...handlers} data-testid="carousel-stage" translateX={centered ? ((sliderItemWidth / 2) as any) : 0}>
      <div
        className="rail"
        data-testid="carousel-rail"
        ref={railRef}
        style={{
          display: 'flex',
          transition: 'all 200ms linear',
          width: `${(wrapperWidth / slidesToShow) * slidesCount}px`,
        }}
      >
        {children}
      </div>
    </StageWrapper>
  );
};
